import React, { useEffect, useState, useMemo } from 'react'
import { FaAngleLeft, FaArrowTrendUp, FaArrowTrendDown, FaWallet } from 'react-icons/fa6';
import { FaSearch } from "react-icons/fa";
import { MdOutlineDateRange } from "react-icons/md";
import { BsDownload, BsPlusCircle, BsSendArrowUp, BsSendArrowDown } from "react-icons/bs";
import { FiArrowDownLeft, FiArrowUpRight, FiFilter } from "react-icons/fi";
import { BiSolidBank } from "react-icons/bi";
import { MdOutlineReceiptLong } from "react-icons/md";
import { SiConvertio } from 'react-icons/si';
import { Link, useNavigate } from 'react-router-dom';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { fetchTransactions, getFormattedAmount } from '../../../actions/payments';
import './transactions.css';
import { refreshUserDetails } from '../../../actions/user';
import { useDispatch } from 'react-redux';
import Pagination from '../../../components/Pagination/Pagination';
import Loader from '../../../components/Loader/Loader';
import Logo from '../../../assets/img/Cardplot-dark.png';

const typeIcons = {
  topup: <BsPlusCircle size={18} />,
  transfer_in: <FiArrowDownLeft size={18} />,
  transfer_out: <FiArrowUpRight size={18} />,
  withdrawal: <BiSolidBank size={18} />,
  fee: <MdOutlineReceiptLong size={18} />,
};



const txnTypes = {
  credit: "Credit",
  debit: "Debit",
};

const statementPageSize = 100;

const getIsoDateRange = (range = []) => {
    const [rawStart, rawEnd] = range || [];
    const startDateValue = rawStart ? new Date(rawStart) : null;
    const endDateValue = rawEnd ? new Date(rawEnd) : null;

    if (startDateValue) {
        startDateValue.setHours(0, 0, 0, 0);
    }

    if (endDateValue) {
        endDateValue.setHours(23, 59, 59, 999);
    }

    return {
        start: startDateValue ? startDateValue.toISOString() : undefined,
        end: endDateValue ? endDateValue.toISOString() : undefined,
    };
};

const htmlValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return stringValue
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const formatStatementDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatStatementDay = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const getPurposeLabel = (transaction) => (
    transaction?.purpose === 'statutory_fee' && transaction?.metadata?.statutory_fee_label
        ? transaction.metadata.statutory_fee_label
        : transaction?.purpose?.replace(/_/g, ' ') || txnTypes[transaction?.type] || transaction?.type || ''
);

const getStatementStatus = (transaction) => {
    const isFee = ['fee', 'statutory_fee'].includes(transaction?.purpose);
    return isFee && transaction?.status === 'outstanding' ? 'completed' : transaction?.status || '';
};

const getTransactionBalance = (transaction) => (
    transaction?.balance_after
    || transaction?.wallet_balance_after
    || transaction?.metadata?.balance_after
    || transaction?.metadata?.wallet_balance_after
    || ''
);

const formatMoney = (amount, currencySymbol = '') => {
    if (amount === '' || amount === null || amount === undefined) return '';
    return `${currencySymbol}${getFormattedAmount(Number(amount || 0))}`;
};

const Transactions = ({ userDetails }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [userTransactions, setUserTransactions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [activeFilter, setActiveFilter] = useState('all'); // all, credit, debit
    const [downloadingStatement, setDownloadingStatement] = useState(false);

    const getUserTransactions = async (pageNumber = 1, range = dateRange) => {
        setLoading(true);
        const { start, end } = getIsoDateRange(range);

        try {
            const response = await fetchTransactions(itemsPerPage, pageNumber, start, end);
            const { data, current_page, last_page } = response.data.transactions;
            setUserTransactions(data);
            setCurrentPage(current_page);
            setTotalPages(last_page);
        } catch (error) {
            // console.error("Error fetching transactions:", error);
        } finally {
            setLoading(false);
        }
    };

    const applyTransactionDateRange = (range) => {
        setDateRange(range);

        if (currentPage === 1) {
            getUserTransactions(1, range);
        } else {
            setCurrentPage(1);
        }
    };

    useEffect(() => {
        getUserTransactions(currentPage);
    }, [currentPage]);

    const goToTransactionDetails = (refId, transaction) => {
        navigate(`/user/transactions/${refId}`, { state: { transactionDetails: transaction } });
    }

    const buildStatementHtml = (statementMeta, headers, transactionRows, title) => {
        return `
            <!doctype html>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <title>${htmlValue(title || 'Cardplot Statement of Account')}</title>
                    <style>
                        * { box-sizing: border-box; margin: 0; padding: 0; }
                        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color: #000000; padding: 20px; background: #FFFFFF; }
                        .doc-card { border: 1.5px solid #000000; padding: 28px; max-width: 850px; margin: 0 auto; background: #FFFFFF; }
                        .accent-bar { display: flex; gap: 2px; height: 24px; margin-bottom: 16px; }
                        .accent-yellow { width: 8px; background-color: #FDCA00; border-radius: 2px 0 0 2px; }
                        .accent-green { width: 8px; background-color: #10B981; border-radius: 0 2px 2px 0; }
                        .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                        .title-box { border: 1px solid #000000; padding: 6px 14px; font-weight: 700; font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase; }
                        .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #475569; margin-top: 24px; margin-bottom: 8px; letter-spacing: 0.5px; }
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid #000000; padding: 6px 10px; font-size: 11px; text-align: left; vertical-align: middle; }
                        .meta-table { width: 60%; max-width: 480px; margin-bottom: 16px; }
                        .meta-table th { width: 140px; font-weight: 500; color: #1E293B; background: #FFFFFF; }
                        .meta-table td { font-weight: 700; color: #000000; }
                        .overview-table { margin-bottom: 16px; width: 80%; }
                        .overview-table th { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #000000; background: #FFFFFF; text-align: center; }
                        .overview-table td { text-align: center; font-size: 12px; font-weight: 700; }
                        .inflow-text { color: #10B981; font-weight: 700; }
                        .txn-table { margin-top: 8px; }
                        .txn-table th { background-color: #121B3C; color: #FFFFFF; font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 8px 10px; border: 1px solid #000000; }
                        .txn-table td { font-size: 10px; }
                        .status-badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 9px; font-weight: 700; text-transform: capitalize; border: 1.5px solid transparent; }
                        .status-badge.completed { border-color: #10B981; color: #10B981; background-color: #FFFFFF; }
                        .status-badge.failed { border-color: #EF4444; color: #EF4444; background-color: #FFFFFF; }
                        .status-badge.pending { border-color: #F59E0B; color: #F59E0B; background-color: #FFFFFF; }
                        @media print {
                            body { padding: 0; background: #FFFFFF; }
                            .doc-card { border: 1.5px solid #000000 !important; margin: 0 !important; width: 100% !important; max-width: 100% !important; }
                            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        }
                    </style>
                </head>
                <body>
                    <div class="doc-card"> 
                        <div class="header-row">
                            <div class="logo">
                                <img src="${Logo}" height="32" alt="Cardplot" />
                            </div>
                            <div class="title-box">STATEMENT OF ACCOUNT</div>
                        </div>

                        <div class="section-title">ACCOUNT SUMMARY</div>
                        <table class="meta-table">
                            <tbody>
                                <tr>
                                    <th>Account Name</th>
                                    <td>${htmlValue(statementMeta.accountName)}</td>
                                </tr>
                                <tr>
                                    <th>Address:</th>
                                    <td>${htmlValue(statementMeta.address)}</td>
                                </tr>
                                <tr>
                                    <th>Currency</th>
                                    <td>${htmlValue(statementMeta.currency)}</td>
                                </tr>
                                <tr>
                                    <th>Transaction Type</th>
                                    <td>${htmlValue(statementMeta.typeFilter)}</td>
                                </tr>
                                <tr>
                                    <th>Statement Period</th>
                                    <td>${htmlValue(statementMeta.dateFilter)}</td>
                                </tr>
                                <tr>
                                    <th>Generated At</th>
                                    <td>${htmlValue(statementMeta.generatedAt)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="section-title">PERIOD OVERVIEW</div>
                        <table class="overview-table">
                            <thead>
                                <tr>
                                    <th>TOTAL INFLOW</th>
                                    <th>TOTAL OUTFLOW</th>
                                    <th>TRANSACTIONS</th>
                                    <th>CURRENT BALANCE</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="inflow-text">${htmlValue(statementMeta.totalCredit)}</td>
                                    <td>${htmlValue(statementMeta.totalDebit)}</td>
                                    <td>${htmlValue(statementMeta.totalTransactions)}</td>
                                    <td>${htmlValue(statementMeta.currentBalance)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="section-title">TRANSACTIONS</div>
                        <table class="txn-table">
                            <thead>
                                <tr>
                                    <th style="width: 18%;">DATE</th>
                                    <th style="width: 18%;">TYPE</th>
                                    <th style="width: 24%;">REFERENCE</th>
                                    <th style="width: 14%;">MONEY IN</th>
                                    <th style="width: 14%;">MONEY OUT</th>
                                    <th style="width: 12%;">STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${transactionRows.map(row => `
                                    <tr>
                                        <td>${htmlValue(row.date)}</td>
                                        <td>${htmlValue(row.type)}</td>
                                        <td>${htmlValue(row.reference)}</td>
                                        <td class="inflow-text">${htmlValue(row.moneyIn)}</td>
                                        <td style="font-weight: 700;">${htmlValue(row.moneyOut)}</td>
                                        <td>
                                            <span class="status-badge ${row.status.toLowerCase()}">${htmlValue(row.status.charAt(0).toUpperCase() + row.status.slice(1))}</span>
                                        </td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                </body>
            </html>
        `;
    };

    const printStatementPdf = (htmlContent, title) => {
        const iframe = document.createElement('iframe');

        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.title = title || 'Cardplot Statement PDF';

        document.body.appendChild(iframe);

        const iframeWindow = iframe.contentWindow;
        const iframeDocument = iframeWindow?.document;
        let hasPrinted = false;

        if (!iframeWindow || !iframeDocument) {
            document.body.removeChild(iframe);
            window.alert('Unable to prepare the PDF statement. Please try again.');
            return;
        }

        const printFrame = () => {
            if (hasPrinted) return;
            hasPrinted = true;
            const originalTitle = document.title;
            if (title) document.title = title;
            iframeWindow.focus();
            iframeWindow.print();
            setTimeout(() => {
                if (title) document.title = originalTitle;
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            }, 1000);
        };

        iframe.onload = printFrame;
        iframeDocument.open();
        iframeDocument.write(htmlContent);
        if (title && iframeDocument) {
            iframeDocument.title = title;
        }
        iframeDocument.close();
        setTimeout(printFrame, 300);
    };

    const handleDownloadStatement = async () => {
        if (downloadingStatement) return;

        setDownloadingStatement(true);
        const { start, end } = getIsoDateRange(dateRange);
        const hasPartialDateRange = Boolean(startDate) !== Boolean(endDate);

        try {
            if (hasPartialDateRange) {
                window.alert('Please select both a start date and an end date before downloading a statement.');
                return;
            }

            const transactions = [];
            let pageNumber = 1;
            let lastPage = 1;

            do {
                const response = await fetchTransactions(statementPageSize, pageNumber, start, end);
                const transactionPage = response?.data?.transactions;
                const pageTransactions = Array.isArray(transactionPage?.data) ? transactionPage.data : [];

                transactions.push(...pageTransactions);
                lastPage = Number(transactionPage?.last_page) || pageNumber;
                pageNumber += 1;
            } while (pageNumber <= lastPage);

            const statementTransactions = activeFilter === 'all'
                ? transactions
                : transactions.filter(transaction => transaction.type === activeFilter);
            const sortedStatementTransactions = [...statementTransactions].sort((firstTransaction, secondTransaction) => (
                new Date(firstTransaction.created_at) - new Date(secondTransaction.created_at)
            ));

            if (!sortedStatementTransactions.length) {
                window.alert('No transactions found for this statement.');
                return;
            }

            const income = sortedStatementTransactions
                .filter(transaction => transaction.type === 'credit')
                .reduce((total, transaction) => total + Number(transaction.amount || 0), 0);
            const expense = sortedStatementTransactions
                .filter(transaction => transaction.type === 'debit')
                .reduce((total, transaction) => total + Number(transaction.amount || 0), 0);
            const accountName = [
                userDetails?.first_name,
                userDetails?.last_name,
            ].filter(Boolean).join(' ') || userDetails?.name || userDetails?.email || 'Cardplot User';
            const address = userDetails?.address || userDetails?.registered_address || '10 Toyin Street, Ikeja, Lagos';
            const dateFilter = startDate && endDate
                ? `${formatStatementDay(startDate)} – ${formatStatementDay(endDate)}`
                : 'All time';
            const typeFilter = activeFilter === 'all' ? 'All transactions' : txnTypes[activeFilter];
            const currencySymbol = userDetails?.wallet?.currency_symbol || sortedStatementTransactions[0]?.wallet?.currency_symbol || '\u20A6';
            const currencyCode = userDetails?.wallet?.currency_code || sortedStatementTransactions[0]?.wallet?.currency_code || 'NGN';
            const currencyName = currencyCode === 'NGN' ? 'NGN - Nigerian Naira' : `${currencyCode}`;
            const currentBalance = userDetails?.wallet?.balance ?? userDetails?.default_wallet?.balance ?? '';
            const generatedAt = new Date();
            const headers = ['DATE', 'TYPE', 'REFERENCE', 'MONEY IN', 'MONEY OUT', 'STATUS'];
            const transactionRows = sortedStatementTransactions.map(transaction => {
                const amount = Number(transaction.amount || 0);
                const narration = getPurposeLabel(transaction);
                const statusStr = getStatementStatus(transaction);

                return {
                    date: formatStatementDate(transaction.created_at),
                    type: narration,
                    reference: transaction.reference || transaction.id || '',
                    moneyIn: transaction.type === 'credit' ? formatMoney(amount, currencySymbol) : '',
                    moneyOut: transaction.type === 'debit' ? formatMoney(amount, currencySymbol) : '',
                    status: statusStr,
                };
            });
            const statementMeta = {
                generatedAt: formatStatementDate(generatedAt),
                accountName,
                address,
                currency: currencyName,
                email: userDetails?.email || '',
                wallet: currencyCode || currencySymbol,
                dateFilter,
                typeFilter,
                totalTransactions: sortedStatementTransactions.length,
                totalCredit: formatMoney(income, currencySymbol),
                totalDebit: formatMoney(expense, currencySymbol),
                currentBalance: currentBalance !== '' ? formatMoney(currentBalance, currencySymbol) : 'N/A',
            };

            const formatDateStr = (d) => {
                const dateObj = new Date(d);
                if (Number.isNaN(dateObj.getTime())) return '';
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            let fileDateRange = '';
            if (startDate && endDate) {
                fileDateRange = `${formatDateStr(startDate)}-to-${formatDateStr(endDate)}`;
            } else if (sortedStatementTransactions.length) {
                const firstTx = sortedStatementTransactions[0];
                const lastTx = sortedStatementTransactions[sortedStatementTransactions.length - 1];
                fileDateRange = `${formatDateStr(firstTx.created_at)}-to-${formatDateStr(lastTx.created_at)}`;
            } else {
                fileDateRange = formatDateStr(generatedAt);
            }

            const statementFileName = `cardplot-statement-${fileDateRange}`;

            const htmlContent = buildStatementHtml(statementMeta, headers, transactionRows, statementFileName);
            printStatementPdf(htmlContent, statementFileName);
        } catch (error) {
            // console.error('Error downloading statement:', error);
            window.alert('Unable to download statement. Please try again.');
        } finally {
            setDownloadingStatement(false);
        }
    };

    // Group transactions by date
    const groupedTransactions = useMemo(() => {
        if (!userTransactions) return {};
        
        const filtered = userTransactions.filter(txn => {
            if (activeFilter === 'all') return true;
            return txn.type === activeFilter;
        });

        return filtered.reduce((groups, txn) => {
            const date = new Date(txn.created_at).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
            });
            
            const today = new Date().toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
            });

            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
            });

            let groupName = date;
            if (date === today) groupName = "Today";
            else if (date === yesterdayStr) groupName = "Yesterday";

            if (!groups[groupName]) groups[groupName] = [];
            groups[groupName].push(txn);
            return groups;
        }, {});
    }, [userTransactions, activeFilter]);

    // Calculate totals for summary cards
    const stats = useMemo(() => {
        const income = userTransactions?.filter(t => t.type === 'credit').reduce((acc, t) => acc + parseFloat(t.amount), 0) || 0;
        const expense = userTransactions?.filter(t => t.type === 'debit').reduce((acc, t) => acc + parseFloat(t.amount), 0) || 0;
        return { income, expense };
    }, [userTransactions]);

  return (
    <section className='main p-2 transactions__page'>
        <div className="container-fluid p-0" >
            <main>
                {/* Overview Cards */}
                {/* <div className="txn-overview">
                    <div className="txn-stat-card">
                        <div className="stat-icon income">
                            <FaArrowTrendUp />
                        </div>
                        <div className="stat-info">
                            <h4>Total Income</h4>
                            <p>{userDetails?.wallet?.currency_symbol || '₦'}{getFormattedAmount(stats.income)}</p>
                        </div>
                    </div>
                    <div className="txn-stat-card">
                        <div className="stat-icon expense">
                            <FaArrowTrendDown />
                        </div>
                        <div className="stat-info">
                            <h4>Total Expenses</h4>
                            <p>{userDetails?.wallet?.currency_symbol || '₦'}{getFormattedAmount(stats.expense)}</p>
                        </div>
                    </div>
                    <div className="txn-stat-card">
                        <div className="stat-icon balance">
                            <FaWallet />
                        </div>
                        <div className="stat-info">
                            <h4>Total Balance</h4>
                            <p>{userDetails?.wallet?.currency_symbol || '₦'}{getFormattedAmount(userDetails?.wallet?.balance || 0)}</p>
                        </div>
                    </div>
                </div> */}

                <div className="transaction-filter">
                    <div className="quick-filters">
                        <button 
                            className={`filter-chip ${activeFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('all')}
                        >
                            All
                        </button>
                        <button 
                            className={`filter-chip ${activeFilter === 'credit' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('credit')}
                        >
                            Income
                        </button>
                        <button 
                            className={`filter-chip ${activeFilter === 'debit' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('debit')}
                        >
                            Expense
                        </button>
                    </div>

                    <div className="transaction-filter-actions d-flex align-items-center gap-3">
                        <div className="date-picker-wrapper">
                            <MdOutlineDateRange size={20} color="#94A3B8"/>
                            <div className="date-picker">
                                <DatePicker
                                    selectsRange
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={(update) => {
                                        if (!update || (update[0] === null && update[1] === null)) {
                                            applyTransactionDateRange([null, null]);
                                        } else if (update[0] && update[1]) {
                                            applyTransactionDateRange(update);
                                        } else {
                                            setDateRange(update);
                                        }
                                    }}
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-control"
                                />
                            </div>
                        </div>

                        {startDate && endDate && (
                            <button
                                className="btn btn-sm btn-primary px-3 rounded-pill"
                                onClick={() => applyTransactionDateRange([startDate, endDate])}
                            >
                                Apply
                            </button>
                        )}

                        <button
                            title='Download Statement (PDF)'
                            className="download-action"
                            onClick={handleDownloadStatement}
                            disabled={downloadingStatement}
                            aria-label="Download Statement (PDF)"
                        >
                            {downloadingStatement ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <BsDownload size={18} />
                            )}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center w-100 py-5">
                        <Loader text="Loading transactions..." />
                    </div>
                ) : (
                    <div className="txn-list-container">
                        {Object.keys(groupedTransactions).length > 0 ? (
                            Object.entries(groupedTransactions).map(([date, txns]) => (
                                <div key={date} className="txn-list-group">
                                    <h2 className="txn-date-header">{date}</h2>
                                    {txns.map((transaction) => {
                                        const txnIsFee = ['fee', 'statutory_fee'].includes(transaction.purpose);
                                        const status = txnIsFee && transaction.status == 'outstanding' ? 'completed' : transaction.status;
                                        const purpose = (transaction.purpose == 'statutory_fee' && transaction.metadata?.statutory_fee_label) ? transaction.metadata?.statutory_fee_label : transaction.purpose; 
                                        return (
                                            <div 
                                                key={transaction.id} 
                                                className="txn-item"
                                                onClick={() => goToTransactionDetails(transaction.id, transaction)}
                                            >
                                                <div className="txn-item-left">
                                                    <div className={`txn-icon-box ${transaction.type}`}>
                                                        {typeIcons[purpose] || (transaction.type === 'credit' ? <BsSendArrowDown /> : <BsSendArrowUp />)}
                                                    </div>
                                                    <div className="txn-main-info">
                                                        <h3 className="text-capitalize">
                                                            {/* {purpose?.replace('_', ' ') || txnTypes[transaction.type]} */}
                                                            {transaction.purpose_of_transaction}
                                                        </h3>
                                                        <p>{new Date(transaction.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                    </div>
                                                </div>
                                                <div className="txn-item-right">
                                                    <div className={`txn-amount ${transaction.type}`}>
                                                        {transaction.type === 'credit' ? '+' : '-'}
                                                        {transaction.wallet?.currency_symbol || transaction.wallet?.currency || transaction.currency}
                                                        {getFormattedAmount(transaction.amount)}
                                                    </div>
                                                    <span className={`txn-status-badge status-${status}`}>
                                                        {status}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-5">
                                <div className="mb-3">
                                    <FiFilter size={48} color="#E2E8F0" />
                                </div>
                                <h4 className="text-muted">No transactions found</h4>
                                <p className="text-muted small">Try adjusting your filters or date range</p>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="mt-4">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={(pageNum) => setCurrentPage(pageNum)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    </section>
  )
}

export default Transactions
